import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AppSharedModule } from 'app/app-shared.module';
import { MACHINE_I18N_PREFIX } from './machine-signal-catalog';
import { MACHINE_UPDATE_OUTDATED_THRESHOLD_MS } from './machine-telemetry.decoder';
import {
  MachineFaultStatus,
  MachineFaultView,
  MachineLampSeverity,
  MachineLampView,
  MachineLinkState,
  MachineSignalState,
  MachineTelemetryView,
} from './machine-telemetry.model';

const MILLISECONDS_PER_SECOND = 1000;
const FAULTS_PREFIX = `${MACHINE_I18N_PREFIX}.faults`;

export const MACHINE_TAB_TIME_FORMAT = 'HH:mm:ss';

const LINK_MODIFIERS: Record<MachineLinkState, string> = {
  [MachineLinkState.Receiving]: 'receiving',
  [MachineLinkState.NoCommunication]: 'lost',
  [MachineLinkState.EngineNotDetected]: 'undetected',
};

const LAMP_CHIP_MODIFIERS: Record<MachineLampSeverity, string> = {
  [MachineLampSeverity.Danger]: 'danger',
  [MachineLampSeverity.Warning]: 'warning',
  [MachineLampSeverity.Ok]: 'neutral',
  [MachineLampSeverity.Muted]: 'muted',
};

let nextInstanceId = 0;

/**
 * Machine telemetry tab of the fleet monitoring screen: link status, summary
 * tiles, and two accordions (faults and every signal) that start closed.
 */
@Component({
  standalone: true,
  imports: [AppSharedModule, DatePipe, NgTemplateOutlet],
  selector: 'app-machine-tab',
  templateUrl: './machine-tab.component.html',
  styleUrls: ['./machine-tab.component.scss'],
})
export class MachineTabComponent {
  @Input() machineTab: MachineTelemetryView;

  @ViewChild('faultsSection') faultsSection: ElementRef<HTMLElement>;

  readonly timeFormat = MACHINE_TAB_TIME_FORMAT;
  readonly outdatedThresholdSeconds =
    MACHINE_UPDATE_OUTDATED_THRESHOLD_MS / MILLISECONDS_PER_SECOND;
  readonly linkState = MachineLinkState;
  readonly signalState = MachineSignalState;
  readonly faultStatus = MachineFaultStatus;
  /** Keeps the accordion ids unique when the desktop and mobile tabs both render. */
  readonly idPrefix = `machine-tab-${nextInstanceId++}`;

  faultsOpen = false;
  signalsOpen = false;

  get linkModifier(): string {
    return LINK_MODIFIERS[this.machineTab.link];
  }

  get isEngineDetected(): boolean {
    return this.machineTab.link !== MachineLinkState.EngineNotDetected;
  }

  toggleFaults(): void {
    this.faultsOpen = !this.faultsOpen;
  }

  toggleSignals(): void {
    this.signalsOpen = !this.signalsOpen;
  }

  /** The status bar shortcut: opens the faults accordion and brings it into view. */
  openFaults(): void {
    this.faultsOpen = true;
    this.faultsSection?.nativeElement.scrollIntoView({ block: 'start' });
  }

  lampChipModifier(lamp: MachineLampView): string {
    return LAMP_CHIP_MODIFIERS[lamp.severity];
  }

  /** i18n key with the singular or plural form of a count. */
  pluralKey(key: string, count: number): string {
    if (count === 1) {
      return `${key}-one`;
    }
    return `${key}-other`;
  }

  faultTitleKey(fault: MachineFaultView): string {
    if (fault.isEngine) {
      return `${FAULTS_PREFIX}.title-engine`;
    }
    return `${FAULTS_PREFIX}.title-ecu`;
  }
}
